import { IonIcon } from '@ionic/react';
import { Reporter } from 'interfaces/Reporter';
import { buildOutline } from 'ionicons/icons';
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router';
import { prettyPhone } from 'utils/prettyPhone';

const useStyles = createUseStyles({
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    marginTop: '10px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    padding: '15px',
    backgroundColor: 'white',
    display: 'flex',
    position: 'relative',
    cursor: 'pointer',
    transition:
      'box-shadow 0.2s ease, transform 0.1s ease, background-color 0.3s ease, border-color 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0px)',
    },

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',

      '&:hover': {
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.5)',
        transform: 'translateY(-2px)',
      },
    },
  },
  rank: {
    position: 'absolute',
    top: 0,
    left: 0,
    borderWidth: '0 0.5px 0.5px 0',
    borderStyle: 'solid',
    borderColor: 'rgba(0, 0, 0, 0.025)',
    borderRadius: '9px 0 10px 0',
    width: '25px',
    height: '25px',
    backgroundColor: '#F8F9FC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: '#636E7C',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#2a2a2a',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      color: '#b3b3b3',
    },
  },
  primaryText: {
    margin: '0 0 2px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: '#282828',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  secondaryText: {
    margin: '0',
    fontSize: '15px',
    fontWeight: 400,
    color: '#666666',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  count: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px',
    fontWeight: 400,
    color: '#666666',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  icon: {
    border: '0.5px solid rgba(0, 0, 0, 0.025)',
    borderRadius: 100,
    backgroundColor: '#F8F9FC',
    width: '25px',
    height: '25px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease, border-color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#2a2a2a',
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
});

type ResidentCardProps = Pick<Reporter, 'name' | 'email' | 'phone'> & {
  count: number;
  rank: number;
};

const ResidentCard: React.FC<ResidentCardProps> = ({ name, email, phone, count, rank }) => {
  const classes = useStyles();
  const history = useHistory();

  return (
    <div
      className={classes.card}
      role="button"
      aria-label={`View report for ${name}`}
      onClick={() => {
        const residentData = {
          name,
          email: email ?? undefined,
          phone: phone ?? undefined,
        };
        history.push(`/reports/create/resident?residents=${btoa(JSON.stringify(residentData))}`);
      }}
    >
      <div style={{ flexBasis: '15%' }}>
        <div className={classes.rank}>
          <p style={{ margin: 0 }}>{rank}</p>
        </div>
      </div>
      <div style={{ flexBasis: '70%' }}>
        <h2 className={classes.primaryText}>{name}</h2>
        {phone && <p className={classes.secondaryText}>{prettyPhone(phone)}</p>}
      </div>
      <div className={classes.count} style={{ flexBasis: '15%' }}>
        <p className={classes.secondaryText} style={{ margin: '0 5px 0 0' }}>
          {count}
        </p>
        <div className={classes.icon}>
          <IonIcon icon={buildOutline} style={{ height: '18px', width: '18px' }} />
        </div>
      </div>
    </div>
  );
};

export default ResidentCard;
